import { useEffect, useState, useCallback } from "react"
import { supabase, logAuditAction } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { Modal, FormGroup, Input, Select, Textarea } from "@/components/common/Modal"
import { Button } from "@/components/common/Button"
import { Badge } from "@/components/common/Badge"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { Plus, RefreshCw, AlertTriangle } from "lucide-react"
import type { OwnershipTransfer } from "@/types"
import { useAuth } from "@/context/AuthContext"

interface UserOption { id: string; email: string; full_name: string | null }

export function TransfersPage() {
  const { toast } = useToast()
  const { user: adminUser } = useAuth()
  const [transfers, setTransfers]   = useState<OwnershipTransfer[]>([])
  const [loading, setLoading]       = useState(true)
  const [open, setOpen]             = useState(false)
  const [saving, setSaving]         = useState(false)

  const [fromSearch, setFromSearch] = useState("")
  const [toSearch, setToSearch]     = useState("")
  const [fromResults, setFromResults] = useState<UserOption[]>([])
  const [toResults, setToResults]     = useState<UserOption[]>([])
  const [fromUser, setFromUser]     = useState<UserOption | null>(null)
  const [toUser, setToUser]         = useState<UserOption | null>(null)
  const [reason, setReason]         = useState("")
  const [notes, setNotes]           = useState("")
  const [transferEarnings, setTransferEarnings] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from("ownership_transfers")
        .select("*")
        .order("created_at", { ascending: false })
      if (error) throw error
      setTransfers((data ?? []) as OwnershipTransfer[])
    } catch { toast("Failed to load transfers", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  const searchUsers = async (q: string, setResults: (r: UserOption[]) => void) => {
    if (q.length < 2) { setResults([]); return }
    const { data } = await supabase
      .from("users")
      .select("id, email, full_name")
      .or(`email.ilike.%${q}%,full_name.ilike.%${q}%`)
      .limit(5)
    setResults((data ?? []) as UserOption[])
  }

  useEffect(() => { searchUsers(fromSearch, setFromResults) }, [fromSearch])
  useEffect(() => { searchUsers(toSearch, setToResults) }, [toSearch])

  const handleTransfer = async () => {
    if (!fromUser || !toUser || !reason) {
      toast("From user, to user and reason are required", "error"); return
    }
    if (fromUser.id === toUser.id) {
      toast("Cannot transfer to the same user", "error"); return
    }
    setSaving(true)
    try {
      const { error } = await supabase.from("ownership_transfers").insert({
        from_user_id: fromUser.id,
        to_user_id:   toUser.id,
        from_email:   fromUser.email,
        to_email:     toUser.email,
        reason,
        notes: notes || null,
        transfer_earnings: transferEarnings,
        admin_id: adminUser?.id ?? null,
        created_at: new Date().toISOString(),
      })
      if (error) throw error
      await logAuditAction(
        "ownership_transfer",
        fromUser.id,
        `Transferred from ${fromUser.email} to ${toUser.email} — ${reason}`,
        fromUser.email
      )
      toast("Transfer recorded successfully", "success")
      setOpen(false)
      setFromUser(null); setToUser(null); setReason(""); setNotes("")
      load()
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Transfer failed", "error")
    } finally { setSaving(false) }
  }

  const REASON_LABELS: Record<string, string> = {
    user_request: "User Request",
    business_sale: "Business Sale",
    inheritance: "Inheritance",
    admin_action: "Admin Action",
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between">
        <div />
        <div className="flex gap-2">
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setOpen(true)}>
            New Transfer
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Ownership Transfers</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Date", "From", "To", "Reason", "Earnings", "Admin"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={6} /> :
               transfers.length === 0 ? (
                <tr><td colSpan={6}>
                  <EmptyState icon="🔄" title="No transfers yet" subtitle="Ownership transfers will appear here." />
                </td></tr>
               ) : transfers.map(t => (
                <tr key={t.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{t.from_email}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{t.to_email}</td>
                  <td className="px-6 py-4">
                    <Badge variant="info">{REASON_LABELS[t.reason] ?? t.reason}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    {t.transfer_earnings
                      ? <Badge variant="success">Transferred</Badge>
                      : <Badge variant="muted">Not transferred</Badge>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">Admin</td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Transfer Ownership" icon="🔄" size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleTransfer}>Complete Transfer</Button>
          </>
        }
      >
        {/* From user */}
        <FormGroup label="From User" required>
          {fromUser ? (
            <div className="flex items-center justify-between p-3 rounded-xl bg-zimrah-50 border border-zimrah-200">
              <div>
                <p className="font-bold text-zimrah-900 text-sm">{fromUser.full_name ?? "—"}</p>
                <p className="text-xs text-zimrah-600">{fromUser.email}</p>
              </div>
              <button onClick={() => { setFromUser(null); setFromSearch("") }}
                className="text-xs text-red-500 hover:text-red-700 font-semibold">Remove</button>
            </div>
          ) : (
            <div>
              <Input value={fromSearch} onChange={e => setFromSearch(e.target.value)} placeholder="Search by email or name…" />
              {fromResults.length > 0 && (
                <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {fromResults.map(u => (
                    <button key={u.id} onClick={() => { setFromUser(u); setFromSearch(""); setFromResults([]) }}
                      className="w-full text-left px-4 py-2.5 hover:bg-zimrah-50 transition-colors border-b border-gray-50 last:border-0">
                      <p className="font-semibold text-sm text-gray-900">{u.full_name ?? "—"}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </FormGroup>

        {/* To user */}
        <FormGroup label="To User" required>
          {toUser ? (
            <div className="flex items-center justify-between p-3 rounded-xl bg-green-50 border border-green-200">
              <div>
                <p className="font-bold text-green-900 text-sm">{toUser.full_name ?? "—"}</p>
                <p className="text-xs text-green-600">{toUser.email}</p>
              </div>
              <button onClick={() => { setToUser(null); setToSearch("") }}
                className="text-xs text-red-500 hover:text-red-700 font-semibold">Remove</button>
            </div>
          ) : (
            <div>
              <Input value={toSearch} onChange={e => setToSearch(e.target.value)} placeholder="Search by email or name…" />
              {toResults.length > 0 && (
                <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {toResults.map(u => (
                    <button key={u.id} onClick={() => { setToUser(u); setToSearch(""); setToResults([]) }}
                      className="w-full text-left px-4 py-2.5 hover:bg-green-50 transition-colors border-b border-gray-50 last:border-0">
                      <p className="font-semibold text-sm text-gray-900">{u.full_name ?? "—"}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </FormGroup>

        <FormGroup label="Reason" required>
          <Select value={reason} onChange={e => setReason(e.target.value)}>
            <option value="">Select reason…</option>
            <option value="user_request">User Request</option>
            <option value="business_sale">Business Sale</option>
            <option value="inheritance">Inheritance</option>
            <option value="admin_action">Admin Action</option>
          </Select>
        </FormGroup>

        <FormGroup label="Notes">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional context…" />
        </FormGroup>

        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
          <input type="checkbox" checked={transferEarnings} onChange={e => setTransferEarnings(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-zimrah-600 focus:ring-zimrah-500" />
          <span className="text-sm font-semibold text-gray-700">Transfer pending earnings to new owner</span>
        </label>

        <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-100 flex gap-2">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs font-semibold text-amber-700">
            This transfers the matrix position and downline. This action is logged and cannot be easily undone.
          </p>
        </div>
      </Modal>
    </div>
  )
}